import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { nextSession } from "@/data/webinarSessions";
import { track } from "@/lib/analytics";

const KEY = "w3rc_briefing_bar_dismissed";

/**
 * Site-wide notice for The CLARITY Briefing, deliberately limited to the home
 * and pricing pages: the founder judged it too aggressive for this audience
 * everywhere else.
 *
 * Dismissal persists in localStorage, so it stays gone. The session date is
 * read from the published schedule rather than written into the copy, so the
 * bar cannot end up announcing a session that already happened.
 */
export const BriefingBar = () => {
  const session = nextSession();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(KEY) === "1";
    } catch {
      // Private mode: show it rather than fail closed.
      return false;
    }
  });

  if (dismissed || !session) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(KEY, "1");
    } catch (err) {
      console.debug("briefing bar: dismissal not persisted (private mode?)", err);
    }
    track("briefing_bar_dismissed", {});
  };

  return (
    <div data-testid="briefing-bar" className="bg-ink text-paper">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-2 flex items-center justify-center gap-3">
        <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.16em] text-center">
          {/* A week that moved off Thursday leads with the exception, because a
              reader who scans "Thursdays" and stops has the wrong day. The
              series language returns on its own once the moved session passes,
              since the exception fields travel with that session. */}
          {session.dayNote ? (
            <>
              The CLARITY Briefing - Next session: {session.dayNote}, {session.label} &middot; Noon ET
              {session.resumesNote ? <> &middot; {session.resumesNote}</> : null}.{" "}
            </>
          ) : (
            <>The CLARITY Briefing - live Thursdays, noon ET. Next: {session.label}.{" "}</>
          )}
          <Link
            to="/webinar"
            data-testid="briefing-bar-cta"
            onClick={() => track("webinar_cta_click", { source: "announcement-bar" })}
            className="underline underline-offset-4 decoration-1 hover:text-blueprint transition-colors"
          >
            Register free
          </Link>
          .
        </p>
        <button
          type="button"
          onClick={dismiss}
          data-testid="briefing-bar-dismiss"
          aria-label="Dismiss this notice"
          className="shrink-0 text-paper/60 hover:text-paper transition-colors"
        >
          <X size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

export default BriefingBar;
